import React, { useMemo, useState } from "react"
import CustomDateRange from "./CustomDateRange"
import dayjs from "dayjs"
import generateInvoicePDF from "./generateInvoicePDF"
import Select from "react-select"
import TransactionRow from "./TransactionRow"
import { Button } from "../../../../components/ui/button"
import { SvgDownload } from "../../../../components/icons"
import { useNavigate } from "react-router-dom"
import generateTransactionsReportPDF from "./generateTransactionsReportPDF"

interface Props {
  transactions: any[]
  type: "past" | "upcoming"
  user: any
  setSubTab?: (tab: "past" | "upcoming") => void
  localPaymentMethods: any[]
}

const UserTransactions: React.FC<Props> = ({
  transactions,
  type,
  user,
  setSubTab,
  localPaymentMethods,
}) => {
  const [visiblePastCount, setVisiblePastCount] = useState(10)
  const [visibleUpcomingCount, setVisibleUpcomingCount] = useState(10)
  const [selectedOptions, setSelectedOptions] = useState<any[]>([])
  const [startDate, setStartDate] = useState<string>("")
  const [endDate, setEndDate] = useState<string>("")
  const navigate = useNavigate()

  const handleBulkDownload = () => {
    const subxPayments = filteredData.filter((t) =>
      t.description?.toLowerCase().includes("subscription")
    )

    const coachPurchases = filteredData.filter(
      (t) => !t.description?.toLowerCase().includes("subscription")
    )

    generateTransactionsReportPDF(
      subxPayments,
      coachPurchases,
      user,
      type === "past" ? "Past Transactions" : "Upcoming Transactions"
    )
  }

  const paid = transactions.filter((t) => {
    const amount = parseFloat(t.amount || 0)
    const status = t.status?.toLowerCase()

    if (status === "paid") return true
    if (status === "denied") return true
    if (status === "scheduled" && amount <= 0) return true
    return false
  })

  const pending = transactions.filter((t) => {
    const amount = parseFloat(t.amount || 0)
    const status = t.status?.toLowerCase()

    if (status === "scheduled" && amount > 0) return true
    if (status === "declined" || status === "denied") return true
    return false
  })

  const data = type === "past" ? paid : pending
  const visibleCount = type === "past" ? visiblePastCount : visibleUpcomingCount

  const uniqueSellers = useMemo(() => {
    const sellers = data.map((t) => {
      const isSubscription = t.description
        ?.toLowerCase()
        .includes("subscription")
      return isSubscription ? "Zoee" : t.merchant || t.coach_name || "Coach"
    })
    return Array.from(new Set(sellers)).sort((a, b) => a.localeCompare(b))
  }, [data])

  const uniqueTypes = useMemo(() => ["Service", "Zoee Subscription"], [])

  const filterOptions = useMemo(() => {
    const baseOptions = [
      {
        label: "SELLER",
        options:
          uniqueSellers.length > 0
            ? uniqueSellers.map((s) => ({
                value: `seller_${s}`,
                label: s,
                group: "seller",
              }))
            : [{ value: "none", label: "No Sellers", isDisabled: true }],
      },
    ]

    if (user?.isCoach) {
      baseOptions.push({
        label: "TYPE",
        options: uniqueTypes.map((t) => ({
          value: `type_${t}`,
          label: t,
          group: "type",
        })),
      })
    }

    if (type === "upcoming") {
      baseOptions.push({
        label: "STATUS",
        options: [
          { value: "status_scheduled", label: "Scheduled", group: "status" },
          { value: "status_overdue", label: "Overdue", group: "status" },
        ],
      })
    }

    return baseOptions
  }, [uniqueSellers, uniqueTypes, user?.isCoach, type])

  const filteredData = useMemo(() => {
    let valid = [...data]

    if (startDate) {
      valid = valid.filter((t) =>
        dayjs(t.date_processed || t.date_scheduled || t.date).isAfter(
          dayjs(startDate).subtract(1, "day")
        )
      )
    }
    if (endDate) {
      valid = valid.filter((t) =>
        dayjs(t.date_processed || t.date_scheduled || t.date).isBefore(
          dayjs(endDate).add(1, "day")
        )
      )
    }

    const selectedSellers = selectedOptions
      .filter((opt) => opt.group === "seller")
      .map((opt) => opt.label)

    if (selectedSellers.length) {
      valid = valid.filter((t) => {
        const isSubscription = t.description
          ?.toLowerCase()
          .includes("subscription")
        const seller = isSubscription
          ? "Zoee"
          : t.merchant || t.coach_name || "Coach"
        return selectedSellers.includes(seller)
      })
    }

    const selectedTypes = selectedOptions
      .filter((opt) => opt.group === "type")
      .map((opt) => opt.label.toLowerCase())

    if (selectedTypes.length) {
      valid = valid.filter((t) => {
        const isSubscription = t.description
          ?.toLowerCase()
          .includes("subscription")
        const type = isSubscription ? "zoee subscription" : "service"
        return selectedTypes.includes(type)
      })
    }

    const selectedStatuses = selectedOptions
      .filter((opt) => opt.group === "status")
      .map((opt) => opt.label.toLowerCase())

    if (selectedStatuses.length && type === "upcoming") {
      valid = valid.filter((t) => {
        const normalized = t.status?.toLowerCase()
        const scheduledDate = dayjs(t.date_scheduled)
        const now = dayjs()
        const numericAmount = parseFloat(String(t.amount ?? "0"))

        const isOverdue =
          (normalized === "scheduled" &&
            numericAmount > 0 &&
            scheduledDate.isBefore(now, "day")) ||
          normalized === "declined"

        const isScheduled =
          normalized === "scheduled" &&
          numericAmount > 0 &&
          !scheduledDate.isBefore(now, "day")

        if (selectedStatuses.includes("overdue") && isOverdue) return true
        if (selectedStatuses.includes("scheduled") && isScheduled) return true

        return false
      })
    }

    return valid
  }, [data, selectedOptions, startDate, endDate, type])

  const getDisplayMethod = (
    method: string,
    amount: string | number | null,
    status?: string
  ) => {
    const numericAmount = parseFloat(String(amount ?? "0"))
    const normalizedStatus = (status || "").toLowerCase()

    if (normalizedStatus === "scheduled" && numericAmount <= 0) return "—"
    if (!method && numericAmount <= 0) return "Free Service"
    if (!method) return "—"

    const lower = method.toLowerCase()
    if (lower.includes("paypal") || lower.includes("@")) return "PayPal"
    if (lower.includes("••••")) return method

    if (
      /\d{4}$/.test(lower) ||
      lower.includes("card") ||
      lower.includes("ending")
    ) {
      const last4 = method.match(/\d{4}$/)?.[0]
      return last4 ? `•••• ${last4}` : "Card"
    }

    return method.charAt(0).toUpperCase() + method.slice(1)
  }

  const handleInvoiceDownload = (transaction: any) => {
    generateInvoicePDF(transaction, user)
  }

  const getStatusAndAction = (
    status: string,
    date_scheduled?: string,
    amount?: string | number | null
  ) => {
    const normalized = status?.toLowerCase()
    const now = dayjs()
    const scheduledDate = dayjs(date_scheduled)
    const numericAmount = parseFloat(String(amount ?? "0"))

    if (
      normalized === "paid" ||
      (normalized === "scheduled" && numericAmount <= 0)
    ) {
      return { label: "Paid", variant: "success", action: "download" }
    }

    if (normalized === "declined") {
      return { label: "Overdue", variant: "error", action: "pay" }
    }

    if (normalized === "scheduled") {
      if (numericAmount > 0 && scheduledDate.isBefore(now, "day")) {
        return { label: "Overdue", variant: "error", action: "pay" }
      }
      return { label: "Scheduled", variant: "neutral", action: "update" }
    }

    return { label: "Unknown", variant: "neutral", action: null }
  }

  const getBadgeClass = (variant: string) => {
    switch (variant) {
      case "success":
        return "bg-green text-foreground border-transparent"
      case "error":
        return "bg-[#FF4A00] text-white border-transparent"
      default:
        return "border border-black text-black"
    }
  }

  const handleLoadMore = () => {
    if (type === "past") setVisiblePastCount((v) => v + 10)
    else setVisibleUpcomingCount((v) => v + 10)
  }

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: "42px",
      height: "42px",
      alignItems: "center",
      padding: "0 4px",
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: "0 6px",
      height: "42px",
      display: "flex",
      alignItems: "center",
    }),
    multiValue: (provided: any) => ({
      ...provided,
      margin: "2px 4px",
      backgroundColor: "#eef2ff",
      borderRadius: "6px",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: "#333",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      ":hover": { backgroundColor: "#ddd", color: "#000" },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? "#E8EFF5"
        : state.isSelected
        ? "#727679"
        : "white",
      color: state.isFocused ? "black" : state.isSelected ? "white" : "#333",
      ":active": {
        backgroundColor: "#727679",
        color: "white",
      },
    }),
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 mt-6">
        {user?.isCoach && (
          <div className="flex flex-col sm:flex-row md:flex-nowrap gap-3 w-full md:w-auto">
            <Button
              variant={type === "past" ? "default" : "outline"}
              onClick={() => setSubTab?.("past")}
              className="flex-1 min-w-[120px] h-[48px] md:h-[56px]"
            >
              Past
            </Button>
            <Button
              variant={type === "upcoming" ? "default" : "outline"}
              onClick={() => setSubTab?.("upcoming")}
              className="flex-1 min-w-[120px] h-[48px] md:h-[56px]"
            >
              Upcoming
            </Button>
          </div>
        )}

        <div className="flex flex-row flex-wrap items-center md:items-end gap-3 w-full md:w-auto md:ml-auto md:justify-end">
          <div className="flex-1 min-w-[150px] max-w-[230px]">
            <CustomDateRange
              onRangeChange={(start, end) => {
                setStartDate(start)
                setEndDate(end)
              }}
            />
          </div>
          <div className="flex-1 min-w-[120px] max-w-[300px]">
            <Select
              classNamePrefix="select"
              options={filterOptions}
              placeholder={
                user?.isCoach
                  ? "Filter by seller or type..."
                  : "Filter by seller..."
              }
              isMulti
              isSearchable={false}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              controlShouldRenderValue
              isClearable
              styles={customStyles}
              value={selectedOptions}
              onChange={(opts: any) => setSelectedOptions(opts || [])}
            />
          </div>
          <div className="flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="hidden sm:flex h-[56px] w-[56px] items-center justify-center"
              onClick={handleBulkDownload}
            >
              <SvgDownload className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="border border-grayMist rounded-lg p-6">
        <div className="hidden lg:grid grid-cols-[1fr_2.5fr_1.5fr_1fr_1fr_1fr] text-[18px] font-semibold border-b pb-2 mb-4">
          <span>Date</span>
          <span>Description</span>
          <span className="text-center">Payment Method</span>
          <span className="text-center">Amount</span>
          <span className="text-center">Status</span>
        </div>

        {filteredData.length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-[16px]">
            No transactions found
          </div>
        ) : (
          <>
            {filteredData
              .sort((a, b) => {
                const dateA = a.date_processed || a.date_scheduled || a.date
                const dateB = b.date_processed || b.date_scheduled || b.date
                return dayjs(dateB).valueOf() - dayjs(dateA).valueOf()
              })
              .slice(0, visibleCount)
              .map((t, i) => {
                const { label, variant, action } = getStatusAndAction(
                  t.status || t.payment_status,
                  t.date_scheduled,
                  t.amount
                )

                const displayMethod = getDisplayMethod(
                  t.paymentMethod,
                  t.amount
                )

                return (
                  <TransactionRow
                    key={i}
                    transaction={t}
                    label={label}
                    variant={variant}
                    action={action}
                    displayMethod={displayMethod}
                    getBadgeClass={getBadgeClass}
                    handleInvoiceDownload={handleInvoiceDownload}
                    navigate={navigate}
                    localPaymentMethods={localPaymentMethods}
                  />
                )
              })}

            {visibleCount < filteredData.length && (
              <div className="flex justify-center mt-6">
                <Button variant="outline" onClick={handleLoadMore}>
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default UserTransactions
